






















import { Vue, Component, Prop, Ref, Watch } from 'vue-property-decorator'
import { NavigationGuardNext, Route } from 'vue-router'
import AlertModule from '@/store/vuex/aplicacao/AlertModule'
import DestaqueDaTrocaDeTela from '@/components/ui/DestaqueDaTrocaDeTela.vue'
import {
	buscarEscolhaDaTelaDeLoja,
	gravarTelaDeLojaEscolhida,
	passouDaViradaDaTelaDeLoja,
	TelaDeLoja,
	telaDeLojaQueAbre,
} from '@/shareds/preferencia-da-tela-de-loja'
import { sairDaBarra, TrocaDeTelaNaBarra, trocaDeTela } from '@/shareds/troca-de-tela'
import { EscolhaDeVersao } from '@/shareds/versao-da-tela'
import { PARAMETRO_POR_ID } from '@/shareds/nova-edicao-de-loja/catalogo-de-parametros'
import { ABA_ANTIGA_DA_SECAO, IdDaSecao } from '@/shareds/nova-edicao-de-loja/secoes'
import { AlteracaoDaLoja } from '@/shareds/nova-edicao-de-loja/alteracoes'
import EdicaoDeLoja from './EdicaoDeLoja/EdicaoDeLoja.vue'
import NovaEdicaoDeLoja from './NovaEdicaoDeLoja/NovaEdicaoDeLoja.vue'
import AvisoDaTelaClassica from './troca-de-tela/AvisoDaTelaClassica.vue'
import DialogoDeTrocaDeTelaDaLoja from './troca-de-tela/DialogoDeTrocaDeTelaDaLoja.vue'
import { TEXTOS_DA_TROCA } from './troca-de-tela/textos-da-troca'

const DURACAO_DO_PULSO_MS = 1300

/** O que esta entrada usa de cada tela para trocar de uma para a outra. */
interface TelaAntiga {
	// podeEditar vem do temAutorizacao: a permissão encontrada (texto), não um booleano
	edicaoGenerica: { temAlteracao: boolean; podeEditar: unknown; validarTransicao: () => Promise<boolean> }
	salvar: () => Promise<boolean>
}

interface TelaNova {
	alteracoes: AlteracaoDaLoja[]
	podeEditar: boolean
	salvar: () => Promise<boolean>
	podeSair: () => Promise<boolean>
}

/**
 * Edição de loja enquanto a tela clássica (abas) e a nova convivem: abre a que vale para o usuário, pela escolha
 * gravada no servidor e pela data da virada, e troca entre elas pelo botão da barra do app. Sai junto com a tela
 * clássica: aí a rota volta a apontar direto para a nova.
 */
@Component({
	components: { EdicaoDeLoja, NovaEdicaoDeLoja, AvisoDaTelaClassica, DestaqueDaTrocaDeTela, DialogoDeTrocaDeTelaDaLoja },
})
export default class EntradaDaEdicaoDeLoja extends Vue {
	@Prop({ type: Boolean }) novo!: boolean
	@Prop({ type: String, default: '' }) id!: string
	@Ref() dialogo!: DialogoDeTrocaDeTelaDaLoja

	TEXTOS = TEXTOS_DA_TROCA
	tela: TelaDeLoja | null = null
	escolhaGravada: EscolhaDeVersao | null = null
	// Vale o momento em que a loja abriu, até a próxima troca: se a virada passar com ela aberta, nada muda no meio do uso
	depoisDaVirada = false
	trocando = false
	destaqueDispensado = false
	pulsarBotao = false
	pulso?: number

	async created() {
		const escolha = await buscarEscolhaDaTelaDeLoja()
		const agora = new Date()
		const tela = telaDeLojaQueAbre(escolha, agora)
		this.escolhaGravada = escolha
		this.depoisDaVirada = passouDaViradaDaTelaDeLoja(agora)
		if (tela === 'antiga') this.levarLinkDaNovaParaAAbaAntiga()
		this.tela = tela
	}

	beforeDestroy() {
		window.clearTimeout(this.pulso)
		sairDaBarra(this.registroNaBarra)
	}

	/** Primeira olhada: antes da virada, na clássica, para quem nunca escolheu entre as duas telas. */
	get mostrarDestaque() {
		return this.tela === 'antiga' && !this.depoisDaVirada && this.escolhaGravada === null && !this.destaqueDispensado
	}

	get registroNaBarra(): TrocaDeTelaNaBarra | null {
		const tela = this.tela
		if (!tela) return null
		const destino: TelaDeLoja = tela === 'antiga' ? 'nova' : 'antiga'
		return {
			destino,
			assunto: 'da loja',
			trocando: this.trocando,
			destacar: this.pulsarBotao,
			trocar: () => this.trocar(destino),
		}
	}

	@Watch('registroNaBarra', { immediate: true })
	registrarNaBarra(registro: TrocaDeTelaNaBarra | null, anterior?: TrocaDeTelaNaBarra | null) {
		if (registro) trocaDeTela.atual = registro
		else sairDaBarra(anterior || null)
	}

	/** Link copiado na nova tela (?secao=, ?p=) aberto por quem usa a clássica: cai na aba onde aquilo fica. */
	levarLinkDaNovaParaAAbaAntiga() {
		const { secao, p, t } = this.$route.query
		if (t || (!secao && !p)) return
		const parametro = typeof p === 'string' ? PARAMETRO_POR_ID[p] : undefined
		const aba = ABA_ANTIGA_DA_SECAO[(parametro ? parametro.assunto : secao) as IdDaSecao]
		this.$router.replace({ query: aba && aba !== 'Geral' ? { t: aba } : {} }).catch(() => undefined)
	}

	// Método e não getter: o getter vira computed com cache, e o $refs não é reativo (ficaria na tela destruída)
	telaAberta() {
		return this.$refs.tela as unknown as TelaAntiga | TelaNova | undefined
	}

	async trocar(destino: TelaDeLoja) {
		const atual = this.telaAberta()
		if (!atual || this.trocando) return

		const alteracoes = this.tela === 'nova' ? (atual as TelaNova).alteracoes.map(({ titulo }) => titulo) : null
		const temAlteracao = alteracoes ? alteracoes.length > 0 : (atual as TelaAntiga).edicaoGenerica.temAlteracao
		// Sem a permissão de alterar loja não há o que salvar, e o diálogo de alterações ofereceria "Salvar e trocar".
		// O que a pessoa mexeu (a tela clássica deixa mexer, só não deixa salvar) fica para trás.
		const podeSalvar = this.tela === 'nova'
			? (atual as TelaNova).podeEditar
			: !!(atual as TelaAntiga).edicaoGenerica.podeEditar

		if (temAlteracao && podeSalvar) {
			const resposta = await this.dialogo.perguntarSobreAlteracoes(destino, alteracoes, this.novo)
			if (resposta === 'continuar') return
			if (resposta === 'salvar' && !(await atual.salvar())) {
				AlertModule.setError(TEXTOS_DA_TROCA.alteracoes.erro)
				return
			}
		}

		const escolha = await this.gravarEscolha(destino)
		if (!escolha) return
		this.depoisDaVirada = passouDaViradaDaTelaDeLoja()
		this.tela = destino
		AlertModule.setInfo(TEXTOS_DA_TROCA.chegou[escolha])
		this.piscarBotao()
	}

	abrirPeloDestaque() {
		this.destaqueDispensado = true
		this.trocar('nova')
	}

	/**
	 * "Agora não" fica gravado como escolha da clássica, que deixa de valer na virada. Se a gravação falhar, o destaque
	 * volta no próximo acesso, como se a pessoa nunca o tivesse visto.
	 */
	dispensarDestaque() {
		this.destaqueDispensado = true
		this.piscarBotao()
		gravarTelaDeLojaEscolhida('antiga')
			.then(escolha => {
				this.escolhaGravada = escolha
			})
			.catch(() => undefined)
	}

	// Um pulso no botão da barra, para mostrar por onde se troca de tela
	piscarBotao() {
		window.clearTimeout(this.pulso)
		this.pulsarBotao = true
		this.pulso = window.setTimeout(() => {
			this.pulsarBotao = false
		}, DURACAO_DO_PULSO_MS)
	}

	async gravarEscolha(tela: TelaDeLoja): Promise<EscolhaDeVersao | null> {
		try {
			this.trocando = true
			this.escolhaGravada = await gravarTelaDeLojaEscolhida(tela)
			return this.escolhaGravada
		} catch (error) {
			AlertModule.setError(TEXTOS_DA_TROCA.erroAoGravar)
			return null
		} finally {
			this.trocando = false
		}
	}

	// A guarda de rota só roda no componente da rota: as duas telas ficam aqui dentro, então ela pergunta à tela aberta
	async beforeRouteLeave(to: Route, from: Route, next: NavigationGuardNext) {
		const atual = this.telaAberta()
		if (!atual) return next()
		const podeSair = this.tela === 'antiga'
			? !(await (atual as TelaAntiga).edicaoGenerica.validarTransicao())
			: await (atual as TelaNova).podeSair()
		next(podeSair ? undefined : false)
	}
}
